import format from "string-format";

import ErrorMessages from "../error-messages";
import { NotSupportedError } from "../errors/not-supported-error";
import { FileVersion } from "../fileversion";
import Guard from "../guard";
import { Compatibility } from "../models/versionining/compatibility";
import { IApiClient } from "./api-client";

export interface IVersionChecker {
    /**
     * Gets the compatibility between a Content Hub instance and SDK.
     * @param serverVersion - File version of a Content Hub instance.
     * @param sdkVersion - File version of the SDK.
     * @param minimumSdkVersion - File version of the minimum SDK version as dictated by the Content Hub instance.
     * @returns The compatibility.
     */
    getCompatibility(
        serverVersion: FileVersion,
        sdkVersion: FileVersion,
        minimumSdkVersion: FileVersion
    ): Compatibility;

    /**
     * Check compatibility of a Content Hub instance with minimum required SDK version.
     * @param requiredVersion - File version required.
     */
    compatibilityCheckAsync(requiredVersion: FileVersion): Promise<void>;

    /**
     * Return compatibility of the Content Hub instance.
     * @param requiredVersion - File version required.
     * @returns True is it's compatible, false otherwise.
     */
    isCompatibleAsync(requiredVersion: FileVersion): Promise<boolean>;
}

export class VersionChecker implements IVersionChecker {
    constructor(private client: IApiClient) {
        Guard.notNull(client);
    }

    getCompatibility(
        serverVersion: FileVersion,
        sdkVersion: FileVersion,
        minimumSdkVersion: FileVersion
    ): Compatibility {
        if (this.isDebugVersion(serverVersion) || this.isDebugVersion(sdkVersion)) {
            return Compatibility.Ok;
        }

        if (sdkVersion.smallerThan(minimumSdkVersion)) {
            return Compatibility.SdkOld;
        }

        if (
            sdkVersion.major > serverVersion.major ||
            (sdkVersion.major === serverVersion.major && sdkVersion.minor > serverVersion.minor)
        ) {
            return Compatibility.SdkNew;
        }

        return Compatibility.Ok;
    }

    async compatibilityCheckAsync(requiredVersion: FileVersion): Promise<void> {
        const apiInfo = await this.client.getApiInfoAsync();

        if (apiInfo.fileVersion.smallerThan(requiredVersion) && !this.isDebugVersion(apiInfo.fileVersion)) {
            throw new NotSupportedError(
                format(ErrorMessages.ContentHubClient.NotSupportedFeature, apiInfo.fileVersion, requiredVersion)
            );
        }
    }

    async isCompatibleAsync(requiredVersion: FileVersion): Promise<boolean> {
        const apiInfo = await this.client.getApiInfoAsync();

        if (apiInfo.fileVersion > requiredVersion || this.isDebugVersion(apiInfo.fileVersion)) {
            return true;
        }

        return false;
    }

    private isDebugVersion(version: FileVersion): boolean {
        return version.major <= 1;
    }
}
